import React from 'react';
import {
  compose,
  setDisplayName,
  withHandlers,
  withProps,
} from 'recompose';
import reactCSS, { hover } from 'reactcss';
import Glyph from '@ux/glyphs';
import { getStyles, modifier, percentage } from '@ux/style-utils';
import { footnote } from '@ux/type-size';
import cssSides, { Side } from '@team-griffin/css-sides';
import longhand from '@team-griffin/css-longhand';
import { gu2px, Curvature } from '@ux/theme-utils';
import { isTrue } from '../../../crosscutting/capra';
import * as r from 'ramda';

export const stylesheet = ({
  theme,
}) => {
  const {
    palette,
    spacing,
    typography,
  } = theme;

  return {
    default: {
      label: {
        position: 'relative',
        display: 'flex',
        height: percentage(100),
        ...cssSides('padding', Side.A, gu2px(2, spacing)),
        ...cssSides('margin', Side.A, 0),
        ...longhand('border', {
          style: 'solid',
          width: 1,
          color: palette.grey11,
        }),
        backgroundColor: '#ffffff',
        color: palette.grey02,
        fontWeight: 600,
        ...footnote(typography),
        cursor: 'pointer',
        alignItems: 'center',
        justifyContent: 'center',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        transition: `
          background-color 0.3s ease-in-out,
          color 0.3s ease-in-out,
          opacity 0.3s ease-in-out
        `,
      },

      input: {
        opacity: 0.01,
        position: 'absolute',
      },

      glyph: {
        display: 'inline-block',
        marginLeft: gu2px(1, spacing),
      },
    },

    hover: {
      label: {
        opacity: 0.8,
      },
    },

    checked: {
      label: {
        backgroundColor: palette.primary,
        color: palette.primaryContrast,
        borderColor: palette.primary,
      },
    },

    [modifier('curvature', Curvature.WEAK)]: {
      label: {
        borderRadius: gu2px(1, spacing),
      },
    },

    [modifier('curvature', Curvature.STRONG)]: {
      label: {
        borderRadius: 25, // arbitrarily large number to cause pill-rounding
      },
    },
  };
};

export const stylesheetToStyles = (stylesheet, props) => reactCSS(stylesheet, {
  checked: props.checked,
  curvature: props.theme.curvature,
  isMinMd: props.isMinLg,
}, props);

export const PureFilterCheckbox = ({
  theme,
  styles,
  name,
  checked,
  pin,
  onChange,
  label,
}) => (
  <label
    style={styles.label}
    htmlFor={pin}
  >
    <input
      type="checkbox"
      style={styles.input}
      id={pin}
      name={name}
      checked={checked}
      onChange={onChange}
    />
    <span style={styles.text}>
      {label}
    </span>
    {r.when(
      isTrue,
      () => (
        <span style={styles.glyph}>
          <Glyph
            theme={theme}
            icon="smallTick"
            color={theme.palette.primaryContrast}
            size={gu2px(3, theme.spacing)}
          />
        </span>
      ),
    )(checked)}
  </label>
);

export const enhance = compose(
  setDisplayName('FilterCheckbox'),
  hover,
  withProps((ownerProps) => ({
    styles: getStyles(stylesheet, stylesheetToStyles, ownerProps),
  })),
);

export const FilterCheckbox = enhance(PureFilterCheckbox);

export const ReduxFormFilterCheckbox = compose(
  setDisplayName('ReduxFormFilterCheckbox'),
  withProps((ownerProps) => ({
    checked: r.pathSatisfies(r.equals(true), [ 'input', 'value' ], ownerProps),
    name: r.path([ 'input', 'name' ], ownerProps),
  })),
  withHandlers({
    onChange: ({
      input,
    }) => (event) => input.onChange(event),
  }),
)(FilterCheckbox);

export default FilterCheckbox;
